import { gql } from "@apollo/client"
import craftApolloClient from "../api/apollo"

import { useContext } from "react"
import { GlobalContext } from '../_app'

import Image from "next/image"
import Link from "next/link"
import {format} from 'date-fns'

import { Header, HeaderShim } from '../../fuselage/global/header/header'
import { Footer } from '../../fuselage/theme/2024/layout/footer/footer'
import { StickySidebar } from "../../fuselage/layout/sticky-sidebar/sticky-sidebar"
import { ProfileCard } from "../../fuselage/components/profile-card/profile-card"
import { ArrowLink } from '../../fuselage/components/arrow-link/arrow-link'
import { ButtonSocial } from '../../fuselage/components/button-social/button-social'
import { VideoPlayer } from "../../fuselage/components/video-player/video-player"
import { Button } from "../../fuselage/components/button/button"


export default function Article ({ entry }) {

    const globals = useContext(GlobalContext)

    // console.log(entry)

    const handleHero = () => {
        if ( entry.hero ) {
            return (
                <div className="container">
                    <Image 
                        src={entry.hero[0].url} 
                        alt="hero" 
                        width={entry.hero[0].width} 
                        height={entry.hero[0].height} 
                        unoptimized
                    />
                </div>
            )
        }
    }

    const handleCategories = () => {
        return (
            <p className="fw-600">
                { entry.contentTypes.map( (type, index) => <span key={type.id}>{index > 0 ? <span className="fw-400 c-primary">&ensp;|&ensp;</span> : ''}{type.title}</span> ) }
            </p>
        )
    }

    const handleProfiles = () => {
        if ( entry.profiles.length ) {
            return (
                <div className="pb-sm bb-1 bc-secondary">
                    <div className="columns-1 gap-xs">
                        <p className="mb-xs fw-600">{ entry.profiles.length > 1 ? 'Authors' : 'Author' }</p>
                        {entry.profiles.map( profile => <ProfileCard key={profile.id} profile={profile} />)}
                    </div>
                </div>
            )
        }
    }

    const handleContentLibraryBlocks = ( entry ) => {

        if ( !entry ) return

        const blocks = entry.contentLibraryContentBlocks

        // console.log('content-library-blocks:', blocks)

        return (
            <>

                {blocks.map( block => {

                    //text
                    if ( block.__typename === 'contentLibraryContentBlocks_text_BlockType' ) {
                        return (
                            <div key={block.id} className="mt-sm formatted">
                                <div dangerouslySetInnerHTML={{__html: block.body}}></div>
                            </div>
                        )
                    }
                    
                    //image
                    if ( block.__typename === 'contentLibraryContentBlocks_image_BlockType' ) {
                        return (
                            <div key={block.id} className="mt-sm">
                                <Image 
                                    src={block.hero[0].url}
                                    width={block.hero[0].width}
                                    height={block.hero[0].height}
                                    layout={`responsive`}
                                    alt=""
                                    unoptimized
                                />
                            </div>
                        )
                    }
                    
                    //video
                    if ( block.__typename === 'contentLibraryContentBlocks_video_BlockType' ) {
                        return (
                            <div key={block.id} className="mt-sm formatted">
                                <VideoPlayer 
                                    videoUrl={block.videoShareUrl}
                                    autoplay={false}
                                    muted={false}
                                    controls={true}
                                />
                            </div>
                        )
                    }
                    
                    //podcast
                    if ( block.__typename === 'contentLibraryContentBlocks_podcast_BlockType' ) {
                        return (
                            <div key={block.id} className="mt-sm formatted">
                                <div dangerouslySetInnerHTML={{__html: block.codeSnippet}}></div>
                            </div>
                        )
                    }
                    
                    //download
                    if ( block.__typename === 'contentLibraryContentBlocks_download_BlockType' ) {
                        return (
                            <div key={block.id} className="mt-sm formatted flex flex-wrap gap-xs">
                                {
                                    block.downloads.map( download => {
                                        return (
                                            <Button 
                                                key={download.id}
                                                href={download.file[0].url}
                                                target={'_blank'}
                                                decal={'fw-600'}
                                                secondary
                                            >
                                                {download.label}
                                            </Button>
                                        )
                                    })
                                }
                            </div>
                        )
                    }

                })}
            </>
        )
    }

    const handleProfileGroups = () => {

        if ( !entry.profileGroups.length ) return

        // console.log('profileGroups:', entry.profileGroups)

        return (
            <div className="pb-sm bb-1 bc-secondary columns-1 gap-xs">
                {
                    entry.profileGroups.map( group => {
                        return (
                            <div key={group.id} className="columns-1 gap-xs">
                                {
                                    group.heading &&
                                    <p className="mb-xs fw-600">{group.heading}</p>
                                }
                                {
                                    group.profiles.length &&
                                    group.profiles.map( profile => <ProfileCard key={profile.id} profile={profile} />)
                                }
                            </div>
                        )
                    })
                }
            </div>
        )
    }
    

    return (
        <>         
            <Header mainMenu={globals.nodes} />
            <HeaderShim mobileOnly/>

            <section className="block bg-default pt-0">
                <div className="container">
                    <StickySidebar size='large' flipped>

                        <main>
                            <HeaderShim/>
                            <div className="mb-sm pb-xs bb-1 bc-secondary flex jc-between">
                               <ArrowLink href="/content-library/press-room" decal='fw-600'>Press Room</ArrowLink>
                               {handleCategories()}                                
                            </div>

                            <p className="fs-1 fw-600 lh-1 c-primary mb-sm">{entry.title}</p>
                            
                            {handleHero()}

                            {handleContentLibraryBlocks( entry )}
                        </main>

                        <aside>
                            <HeaderShim/>
                            <div className="mb-sm pb-xs bb-1 bc-secondary">
                                <p className="fw-600">Published: { format(new Date(entry.postDate), 'd MMMM yyyy') }</p>
                            </div>
                            
                            {handleProfileGroups()}

                            <div className="py-xs">
                                <p className="fw-600 pb-xs">Share this article</p>
                                <div className="flex gap-xxs">
                                    <ButtonSocial icon='linkedin' decal='bg-secondary' />
                                    <ButtonSocial icon='facebook' decal='bg-secondary' />
                                    <ButtonSocial icon='twitter' decal='bg-secondary' />
                                </div>
                            </div>

                        </aside>

                    </StickySidebar>
                </div>                
            </section>

            <Footer 
                socialConnections={globals.globalSets[2].socialConnections} 
                contactInfo={globals.globalSets[2].contactDetails[0].body}
                hubspotForm={globals.globalSets[2].hubspotForm[0] ? globals.globalSets[2].hubspotForm[0] : null}
            />
        </>
    )

}
Article.theme = 'aviadev-2024'

export async function getStaticPaths() {

    const res = await craftApolloClient().query({
        query: gql`
            query Articles {
                entries(site: "${process.env.SITE_HANDLE}", section: "contentLibrary") {
                    slug
                }
            }
        `
    })

    const entries = await res.data.entries

    return {
        paths: entries.map( entry => ({ params: { slug: entry.slug } })),
        fallback: false
    }

}

export async function getStaticProps({ params, preview, previewData }) {

    const entryQuery = await craftApolloClient( preview, previewData ).query({
        query: gql`
            query Entry {
                entries(site: "${process.env.SITE_HANDLE}", slug: "${params.slug}") {
                    ... on contentLibrary_article_Entry {
                        id
                        slug
                        title
                        postDate
                        body
                        hero {
                            id
                            url
                            width
                            height
                        }
                        contentLibraryContentBlocks {
                            ... on contentLibraryContentBlocks_text_BlockType {
                                id
                                body
                            }
                            ... on contentLibraryContentBlocks_video_BlockType {
                                id
                                videoShareUrl
                            }
                            ... on contentLibraryContentBlocks_image_BlockType {
                                id
                                hero {
                                    id
                                    url
                                    width
                                    height
                                }
                            }
                            ... on contentLibraryContentBlocks_download_BlockType {
                                id
                                downloads {
                                    ... on downloads_BlockType {
                                        id
                                        file {
                                            id
                                            url
                                        }
                                        label
                                    }
                                }
                            }
                            ... on contentLibraryContentBlocks_podcast_BlockType {
                                id
                                codeSnippet
                            }
                        }
                        contentTypes {
                            ... on contentLibrary_Category {
                                id
                                title
                                slug
                            }
                        }


                        profiles {
                            id
                            ... on profiles_profile_Entry {
                                id
                                title
                                positions {
                                    ... on positions_BlockType {
                                        id
                                        jobTitle
                                        organisation
                                    }
                                }
                                avatar {
                                    id
                                    url
                                    width
                                    height
                                }
                            }
                        }
                    }

                    profileGroups {
                        id
                        ... on profileGroups_BlockType {
                            id
                            heading
                            profiles {
                                id
                                ... on profiles_profile_Entry {
                                    id
                                    title
                                    positions {
                                        ... on positions_BlockType {
                                            id
                                            jobTitle
                                            organisation
                                        }
                                    }
                                    avatar {
                                        id
                                        url
                                        width
                                        height
                                    }
                                }
                            }
                        }
                    }

                }
            }
        `
    })

    let entry = entryQuery.data.entries[0]

    return { props: { entry }}
}